import { useState, useEffect } from 'react';
import { apiCredentialsApi } from '../../utils/api';
import { Key, Plus, Pencil, Trash2, X, Check, Copy, RefreshCw, AlertTriangle, Eye, EyeOff, Shield, Building2, BookOpen, Globe } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import ApiDocumentation from './ApiDocumentation';

export default function ApiCredentials() {
  const [activeTab, setActiveTab] = useState('credentials');
  const [credentials, setCredentials] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [emisores, setEmisores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [newCredentialSecret, setNewCredentialSecret] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ show: false, type: null, credential: null });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [credRes, permRes, emisoresRes] = await Promise.all([
        apiCredentialsApi.list(),
        apiCredentialsApi.getPermissions(),
        apiCredentialsApi.getEmisores()
      ]);
      setCredentials(credRes.data.data || []);
      setPermissions(permRes.data.data || []);
      setEmisores(emisoresRes.data.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar credenciales');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (cred) => {
    setConfirmModal({ show: true, type: 'delete', credential: cred });
  };

  const handleRegenerate = async (cred) => {
    setConfirmModal({ show: true, type: 'regenerate', credential: cred });
  };

  const executeConfirmedAction = async () => {
    const { type, credential } = confirmModal;
    setConfirmModal({ show: false, type: null, credential: null });

    if (type === 'delete') {
      try {
        await apiCredentialsApi.delete(credential.id);
        toast.success('Credencial eliminada');
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Error al eliminar');
      }
    } else if (type === 'regenerate') {
      try {
        const res = await apiCredentialsApi.regenerateSecret(credential.id);
        setNewCredentialSecret({
          name: credential.name,
          apiKey: res.data.data.apiKey,
          apiSecret: res.data.data.apiSecret
        });
        toast.success('Secret regenerado');
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Error al regenerar');
      }
    }
  };

  const openNew = () => {
    setEditingCredential(null);
    setShowModal(true);
  };

  const openEdit = async (cred) => {
    try {
      const res = await apiCredentialsApi.get(cred.id);
      setEditingCredential(res.data.data);
      setShowModal(true);
    } catch (error) {
      toast.error('Error al cargar credencial');
    }
  };

  const handleSave = async (formData) => {
    try {
      if (editingCredential) {
        await apiCredentialsApi.update(editingCredential.id, formData);
        toast.success('Credencial actualizada');
        setShowModal(false);
        loadData();
      } else {
        const res = await apiCredentialsApi.create(formData);
        setShowModal(false);
        setNewCredentialSecret({
          name: formData.name,
          apiKey: res.data.data.apiKey,
          apiSecret: res.data.data.apiSecret
        });
        toast.success('Credencial creada');
        loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar');
    }
  };

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('credentials')}
          className={clsx('px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2',
            activeTab === 'credentials' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          <Key className="h-4 w-4" /> Credenciales
        </button>
        <button
          onClick={() => setActiveTab('docs')}
          className={clsx('px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2',
            activeTab === 'docs' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          <BookOpen className="h-4 w-4" /> Documentación API
        </button>
      </div>

      {activeTab === 'docs' && <ApiDocumentation />}

      {activeTab === 'credentials' && (<>
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Key className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900">Credenciales para Integraciones</h3>
            <p className="text-sm text-blue-700 mt-1">
              Las credenciales API permiten que sistemas externos (CRM, ERP, eCommerce) se conecten a Finance360.
              Cada credencial se asocia a una empresa específica o a todas las empresas de la cuenta.
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-end">
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
        >
          <Plus size={18} />
          Nueva Credencial
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="mt-2 text-gray-500">Cargando...</p>
          </div>
        ) : credentials.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Key className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No hay credenciales API registradas.</p>
            <p className="text-sm mt-1">Cree una credencial para permitir integraciones externas.</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Empresa</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Nombre</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">API Key</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Permisos</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Último Uso</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {credentials.map((cred) => (
                <tr key={cred.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {cred.emisorId ? (
                        <Building2 className="h-4 w-4 text-blue-500" />
                      ) : (
                        <Globe className="h-4 w-4 text-indigo-500" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{cred.emisorId ? (cred.emisorNombre || 'Empresa') : 'Todas las empresas'}</p>
                        <p className="text-xs text-gray-500">{cred.emisorId ? cred.emisorIdentificacion : 'Acceso a toda la cuenta'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Key className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-900">{cred.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded text-gray-600">
                      {cred.apiKey}
                    </code>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(cred.permissions || ['*']).slice(0, 2).map((perm, idx) => (
                        <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                          <Shield size={10} className="mr-1" />
                          {perm}
                        </span>
                      ))}
                      {(cred.permissions || []).length > 2 && (
                        <span className="text-xs text-gray-500">+{cred.permissions.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={clsx(
                      'px-2 py-1 rounded text-xs font-medium',
                      cred.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    )}>
                      {cred.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {cred.lastUsedAt ? new Date(cred.lastUsedAt).toLocaleString('es-CR') : 'Nunca'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => openEdit(cred)}
                        className="p-1.5 hover:bg-blue-50 rounded"
                        title="Editar"
                      >
                        <Pencil className="h-4 w-4 text-blue-500" />
                      </button>
                      <button
                        onClick={() => handleRegenerate(cred)}
                        className="p-1.5 hover:bg-amber-50 rounded"
                        title="Regenerar Secret"
                      >
                        <RefreshCw className="h-4 w-4 text-amber-500" />
                      </button>
                      <button
                        onClick={() => handleDelete(cred)}
                        className="p-1.5 hover:bg-red-50 rounded"
                        title="Eliminar"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </>)}

      {/* Create/Edit Modal */}
      {showModal && (
        <CredentialModal
          credential={editingCredential}
          permissions={permissions}
          emisores={emisores}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}

      {/* New Credential Secret Modal */}
      {newCredentialSecret && (
        <SecretDisplayModal
          credential={newCredentialSecret}
          onClose={() => setNewCredentialSecret(null)}
        />
      )}

      {/* Confirm Modal */}
      {confirmModal.show && (
        <ConfirmModal
          type={confirmModal.type}
          credential={confirmModal.credential}
          onConfirm={executeConfirmedAction}
          onCancel={() => setConfirmModal({ show: false, type: null, credential: null })}
        />
      )}
    </div>
  );
}

// Credential Create/Edit Modal
function CredentialModal({ credential, permissions, emisores, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: credential?.name || '',
    // Alcance: id de empresa, o 'all' para todas las empresas de la cuenta (emisor_id NULL)
    emisorId: credential ? (credential.emisorId || 'all') : '',
    permissions: credential?.permissions || ['*'],
    autoSubmit: credential?.autoSubmit !== false,
    isActive: credential?.isActive !== false
  });
  const [loading, setLoading] = useState(false);

  const togglePermission = (code) => {
    let newPerms = [...formData.permissions];

    if (code === '*') {
      if (newPerms.includes('*')) {
        toast.error('Seleccione permisos específicos para quitar Acceso Total');
        return;
      } else {
        newPerms = ['*'];
      }
    } else {
      newPerms = newPerms.filter(p => p !== '*');

      if (newPerms.includes(code)) {
        newPerms = newPerms.filter(p => p !== code);
      } else {
        newPerms = [...newPerms, code];
      }

      if (newPerms.length === 0) {
        newPerms = ['*'];
      }
    }

    setFormData({ ...formData, permissions: newPerms });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.emisorId) {
      toast.error('Debe seleccionar una empresa o todas las empresas de la cuenta');
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
    } finally {
      setLoading(false);
    }
  };

  const hasAllAccess = formData.permissions.includes('*');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-5xl mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white">
          <h3 className="text-lg font-semibold">
            {credential ? 'Editar Credencial' : 'Nueva Credencial API'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* 27/10/2026: dos columnas (datos a la izquierda, permisos a la derecha) en vez de una columna larga */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4 min-w-0">
          {/* Alcance: una empresa o todas las empresas de la cuenta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Alcance *</label>
            <select
              value={formData.emisorId}
              onChange={(e) => setFormData({ ...formData, emisorId: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              required
            >
              <option value="">Seleccione el alcance...</option>
              <option value="all">Todas las empresas de la cuenta</option>
              {emisores.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.nombre} ({emp.identificacion})
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {formData.emisorId === 'all'
                ? 'La credencial opera sobre cualquier empresa de la cuenta. En cada llamada se indica la empresa con el header X-Emisor-ID.'
                : 'La credencial permitirá acceso solo a esta empresa'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Ej: Payroll360 Integration"
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Un nombre descriptivo para identificar esta integración</p>
          </div>

          <div className="border rounded-lg p-3">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="text-sm font-medium text-gray-700">Envío directo a Hacienda</p>
                <p className="text-xs text-gray-500">
                  {formData.autoSubmit
                    ? 'Las facturas se envían directamente a Hacienda sin revisión'
                    : 'El sistema externo decide por request si envía a Hacienda (submit: true) o queda en Facturación API'}
                </p>
              </div>
              <div className={`relative w-11 h-6 rounded-full transition-colors ${formData.autoSubmit ? 'bg-green-500' : 'bg-gray-300'}`}
                onClick={() => setFormData({ ...formData, autoSubmit: !formData.autoSubmit })}>
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${formData.autoSubmit ? 'translate-x-5' : ''}`} />
              </div>
            </label>
          </div>
          {credential && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={formData.isActive ? 'true' : 'false'}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'true' })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              >
                <option value="true">Activo</option>
                <option value="false">Inactivo</option>
              </select>
            </div>
          )}

            </div>
            <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">Permisos</label>
            <p className="text-xs text-gray-500 mb-2">
              {hasAllAccess
                ? 'Tiene acceso total. Haga clic en un permiso específico para restringir.'
                : `${formData.permissions.length} permiso(s) seleccionado(s)`}
            </p>
            <div className="border rounded-lg max-h-[60vh] overflow-y-auto">
              {(() => {
                const categorias = {
                  general: { label: 'General', color: 'bg-gray-100 text-gray-700' },
                  facturacion: { label: 'Facturación', color: 'bg-blue-100 text-blue-700' },
                  clientes: { label: 'Clientes', color: 'bg-green-100 text-green-700' },
                  productos: { label: 'Productos / Inventario', color: 'bg-purple-100 text-purple-700' },
                  empresas: { label: 'Empresas', color: 'bg-amber-100 text-amber-700' },
                  contabilidad: { label: 'Contabilidad', color: 'bg-indigo-100 text-indigo-700' },
                  configuracion: { label: 'Configuración', color: 'bg-red-100 text-red-700' },
                  respaldos: { label: 'Respaldos', color: 'bg-teal-100 text-teal-700' },
                  utilidades: { label: 'Utilidades', color: 'bg-cyan-100 text-cyan-700' }
                };
                const grouped = {};
                permissions.forEach(p => {
                  const cat = p.categoria || 'general';
                  if (!grouped[cat]) grouped[cat] = [];
                  grouped[cat].push(p);
                });
                return Object.entries(grouped).map(([cat, perms]) => (
                  <div key={cat}>
                    <div className={`px-3 py-1.5 text-xs font-semibold ${categorias[cat]?.color || 'bg-gray-100 text-gray-700'} sticky top-0`}>
                      {categorias[cat]?.label || cat}
                    </div>
                    {perms.map(perm => {
                      const isSelected = hasAllAccess ? perm.code === '*' : formData.permissions.includes(perm.code);
                      return (
                        <label key={perm.code} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 border-b border-gray-100">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => togglePermission(perm.code)}
                            className="h-4 w-4 text-blue-600 rounded flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{perm.name}</p>
                            <p className="text-xs text-gray-500">{perm.description}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ));
              })()}
            </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50 flex items-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-green-500 border-t-transparent rounded-full"></span>
                  Guardando...
                </>
              ) : (
                <>
                  <Check size={18} />
                  {credential ? 'Actualizar' : 'Crear Credencial'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Confirm Modal
function ConfirmModal({ type, credential, onConfirm, onCancel }) {
  const isDelete = type === 'delete';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-md mx-4 shadow-xl">
        <div className={clsx(
          'flex items-center gap-3 px-6 py-4 border-b',
          isDelete ? 'bg-red-50' : 'bg-amber-50'
        )}>
          <div className={clsx(
            'p-2 rounded-full',
            isDelete ? 'bg-red-100' : 'bg-amber-100'
          )}>
            {isDelete ? (
              <Trash2 className="h-5 w-5 text-red-600" />
            ) : (
              <RefreshCw className="h-5 w-5 text-amber-600" />
            )}
          </div>
          <h3 className={clsx(
            'text-lg font-semibold',
            isDelete ? 'text-red-800' : 'text-amber-800'
          )}>
            {isDelete ? 'Eliminar Credencial' : 'Regenerar API Secret'}
          </h3>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <Key className="h-5 w-5 text-gray-400" />
            <div>
              <p className="font-medium text-gray-900">{credential.name}</p>
              <p className="text-sm text-gray-500">{credential.emisorId ? credential.emisorNombre : 'Todas las empresas'}</p>
            </div>
          </div>

          <p className="text-gray-600">
            {isDelete ? (
              <>
                ¿Está seguro de eliminar esta credencial?
                <span className="font-medium text-red-600"> Esta acción no se puede deshacer</span> y
                cualquier integración que la use dejará de funcionar.
              </>
            ) : (
              <>
                ¿Regenerar el API Secret de esta credencial?
                <span className="font-medium text-amber-600"> El secret anterior dejará de funcionar inmediatamente</span> y
                deberá actualizar la configuración en los sistemas que la usen.
              </>
            )}
          </p>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onCancel}
            className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className={clsx(
              'px-4 py-2 border-2 rounded-lg transition-colors flex items-center gap-2',
              isDelete
                ? 'border-red-500 text-red-600 hover:bg-red-50'
                : 'border-amber-500 text-amber-600 hover:bg-amber-50'
            )}
          >
            {isDelete ? (
              <>
                <Trash2 size={18} />
                Eliminar
              </>
            ) : (
              <>
                <RefreshCw size={18} />
                Regenerar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Secret Display Modal (shown after creating or regenerating)
function SecretDisplayModal({ credential, onClose }) {
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState({ key: false, secret: false });

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ ...copied, [type]: true });
      toast.success('Copiado al portapapeles');
      setTimeout(() => setCopied({ ...copied, [type]: false }), 2000);
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-lg mx-4 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-amber-50">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <h3 className="text-lg font-semibold text-amber-800">Credenciales Generadas</h3>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800 font-medium">
              Guarde estas credenciales de forma segura. El API Secret no se mostrará de nuevo.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <p className="text-gray-900 font-medium">{credential.name}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-gray-100 px-3 py-2 rounded-lg text-sm font-mono break-all">
                {credential.apiKey}
              </code>
              <button
                onClick={() => copyToClipboard(credential.apiKey, 'key')}
                className={clsx(
                  'p-2 rounded-lg transition-colors',
                  copied.key ? 'bg-green-100 text-green-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                )}
              >
                {copied.key ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">API Secret</label>
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <code className="block bg-gray-100 px-3 py-2 rounded-lg text-sm font-mono break-all">
                  {showSecret ? credential.apiSecret : '••••••••••••••••••••••••••••••••'}
                </code>
              </div>
              <button
                onClick={() => setShowSecret(!showSecret)}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600"
              >
                {showSecret ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              <button
                onClick={() => copyToClipboard(credential.apiSecret, 'secret')}
                className={clsx(
                  'p-2 rounded-lg transition-colors',
                  copied.secret ? 'bg-green-100 text-green-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                )}
              >
                {copied.secret ? <Check size={18} /> : <Copy size={18} />}
              </button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Para conectar desde Payroll360:</strong><br />
              1. Copie el API Key y Secret<br />
              2. En Payroll360, vaya a Administración → Empresas → Integración Contable<br />
              3. Pegue las credenciales en la configuración
            </p>
          </div>

          <div className="flex justify-end pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
            >
              Entendido, cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
